import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, throwError } from 'rxjs';
import { CountriesService } from '../../services/utelize/system/mapping/countries-service';
import { CountryDTO } from '../../models/system/mapping/country.dto';
import { DecryptAesClientSide, EncryptAesClientSide } from 'src/app/core/encryption';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PhoneNumberService, PhoneNumberValidationResult } from '../../services/utelize/general/phone-number-service';
import { StorageValues } from 'src/app/core/consts';
import { ValidatorService } from '../../services/utelize/general/validator-service';

@Component({
  selector: 'app-dropdown-country-dialling-code',
  templateUrl: './dropdown-country-dialling-code.component.html',
  styleUrls: ['./dropdown-country-dialling-code.component.scss']
})

export class DropdownCountryDiallingCodeComponent implements AfterViewInit, OnInit {

  @Input() mobileNumber       : null | string | undefined;
  @Input() mobileNumberIntCode: null | string | undefined;
  @Input() requiredAsterisk   : boolean=false;

  @Output() valueChanged: EventEmitter<void>=new EventEmitter<void>();
  
  @ViewChild('dropdownButton') dropdownButton!: ElementRef;

  private countriesData!: CountryDTO[];
  private pinnedCodes   : Set<string>=new Set<string>();

  public countriesDataFiltered!: CountryDTO[];
  public disabled              : boolean=false;
  public formSubmitted         : boolean=false;
  public isRequired            = (path: string) => this.validatorService.isRequired(this.dialCodeForm, path, this.requiredAsterisk);
  public dialCodeForm!         : UntypedFormGroup;
  public selectedCountry       : CountryDTO | undefined;
  public validData             : boolean=false;
  public validationMessage     : string | undefined;

  //#region CRN_20250429 - These are for the parent

  public outIntDialCode     : string | undefined;
  public outMobileNumber    : string | undefined;
  public outFullMobileNumber: string | undefined;

  //#endregion
  
  constructor(
    private authService       : AuthenticationService,
    private countriesService  : CountriesService,
    private exceptionsService : ExceptionsService,
    private formBuilder       : FormBuilder,
    private phoneNumberService: PhoneNumberService,
    private validatorService  : ValidatorService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.disabled=false;
      this.getDropdownData();
      
      if (this.mobileNumber != null && this.mobileNumber != undefined)
      {
        this.mobileNumber=this.phoneNumberService.stripLeadingZeros(this.phoneNumberService.toDigits(this.mobileNumber));

        if (this.mobileNumberIntCode != null && this.mobileNumberIntCode != undefined) {
          let stripped=this.phoneNumberService.stripDuplicatedDialCode(this.mobileNumber, this.mobileNumberIntCode);

          while (stripped.strippedDialCode) {
            this.mobileNumber=stripped.value;
            stripped         =this.phoneNumberService.stripDuplicatedDialCode(this.mobileNumber, this.mobileNumberIntCode);
          }
        }
      }
      
      this.dialCodeForm=this.formBuilder.group({
        mobilenumber: [this.mobileNumber, [Validators.required]],
        search      : [undefined,         []],
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(){
    try {
      const searchButton=this.dropdownButton.nativeElement;

      searchButton.addEventListener('show.bs.dropdown', () => {
        if (this.dialCodeForm && this.dialCodeForm.controls["search"])
          this.dialCodeForm.controls["search"].setValue(undefined);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  get formOfDialCode() {
    return this.dialCodeForm.controls;
  }
  
  private getDropdownData() {
    try {
      this.countriesService.get()
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.countriesData=(data || []).filter((country: any) =>
            country.countryName  !== null && country.countryName  !== undefined &&
            country.diallingCode !== null && country.diallingCode !== undefined
          );

          this.loadPinnedCodes();
          this.countriesData        =this.applyPinnedSort(this.countriesData);
          this.countriesDataFiltered=this.countriesData;
          this.selectedCountry      =this.countriesData.find(x => x.diallingCode==this.mobileNumberIntCode);
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getDropdownDataObjectArray() {
    try {
      return this.countriesData;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }

  public countriesSelectOnChange(data: any) {
    try {
      this.selectedCountry  =data;
      this.validationMessage=undefined;
      this.validData        =false;

      this.valueChanged.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }

  public onMobileNumberChange() {
    try {
      this.validationMessage=undefined;
      this.validData        =false;

      this.valueChanged.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public stripOfLeadingZeros(mobileNumber: string) {
    try {
      return this.phoneNumberService.stripLeadingZeros(mobileNumber);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }

  public onSearchInputChange(event: any) {
    try {
      let search=this.dialCodeForm.controls["search"].value;

      if (search==null || search==undefined || search.trim()=="") {
        this.countriesDataFiltered=this.applyPinnedSort(this.countriesData);
        return;
      }

      search=search.trim().toLowerCase();

      this.countriesDataFiltered=this.applyPinnedSort(this.countriesData.filter(c =>
        c.countryName?.toLowerCase().includes(search) ||
        c.diallingCode?.includes(search)
      ));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }

  private applyPinnedSort(countries: CountryDTO[]): CountryDTO[] {
    try {
      const pinned  : CountryDTO[]=countries.filter(c => this.isPinned(c));
      const unpinned: CountryDTO[]=countries.filter(c => !this.isPinned(c));

      return [...pinned, ...unpinned];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return countries;
    }
  }

  private loadPinnedCodes(): void {
    try {
      const raw: null | string | undefined=DecryptAesClientSide(localStorage.getItem(this.pinnedCodesStorageKey()));

      this.pinnedCodes=new Set<string>(raw ? <string[]>JSON.parse(raw) : []);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private pinnedCodesStorageKey(): string {
    try {
      return `${StorageValues.sureSimDialCodePins_}${this.authService?.currentUserObject()?.userId ?? -1}`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return `${StorageValues.sureSimDialCodePins_}-1`;
    }
  }

  private savePinnedCodes(): void {
    try {
      localStorage.setItem(this.pinnedCodesStorageKey(), EncryptAesClientSide(JSON.stringify([...this.pinnedCodes])));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public isPinned(country: CountryDTO): boolean {
    try {
      return this.pinnedCodes.has(country.alpha2Code);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public togglePinned(country: CountryDTO, event: MouseEvent): void {
    try {
      event.stopPropagation();
      event.preventDefault();

      if (this.pinnedCodes.has(country.alpha2Code))
        this.pinnedCodes.delete(country.alpha2Code);
      else
        this.pinnedCodes.add(country.alpha2Code);

      this.savePinnedCodes();

      this.countriesData=this.applyPinnedSort(this.countriesData);
      this.onSearchInputChange(event);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  public submit() {
    try {
      this.formSubmitted      =true;
      this.outIntDialCode     =undefined;
      this.outMobileNumber    =undefined;
      this.outFullMobileNumber=undefined;
      this.validData          =false;
      this.validationMessage  =undefined;

      if (!this.selectedCountry) {
        this.validationMessage="Select a country code (on left)";
        return;
      }

      const result: PhoneNumberValidationResult=this.phoneNumberService.validateAgainstDialCode(
        this.dialCodeForm.controls["mobilenumber"].value,
        this.selectedCountry.diallingCode,
        this.selectedCountry.alpha2Code
      );

      if (!result.isValid) {
        this.validationMessage=result.errorMessage;
        return;
      }

      if (result.nationalNumber != this.dialCodeForm.controls["mobilenumber"].value)
        this.dialCodeForm.controls["mobilenumber"].setValue(result.nationalNumber);

      this.outIntDialCode     =this.selectedCountry.diallingCode;
      this.outMobileNumber    =result.nationalNumber;
      this.outFullMobileNumber=result.e164;
      this.validData          =true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  public lockDownForm(): void {
    try {
      this.disabled=true;
      this.dialCodeForm.get("mobilenumber")?.disable();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
